// @ts-nocheck
/**
 * Hawkeye metrics: org configuration + digest API + matrix assembly.
 * Used by HawkeyePage (direct fetch) and by useHawkeyeMetrics (wrapper).
 */
import Store from "../../Store/Store";
import {
  fetchHawkeyeDigest,
  fetchMetricsOrgConfiguration,
} from "../../Store/Reducers/MetricsSlice";

export interface MetricValue {
  metric_id: string;
  metric_name: string;
  category: string;
  category_id: string;
  subcategory: string;
  value: string;
  normalized_value: number;
  measurement_date: string;
  unit_of_measure: string;
  format: string;
  scale: string;
  benchmark_percentile?: number;
  previous_value?: string;
  trend_direction?: string;
  data_quality?: string;
  change_percentage?: number;
}

export interface AccountMetrics {
  account_id: string;
  account_name: string;
  metrics: MetricValue[];
}

export interface CategoryScore {
  category: string;
  score: number;
  metrics: MetricValue[];
  aiScore?: number;
}

export interface AccountMatrixData {
  account_id: string;
  account_name: string;
  overallScore: number;
  categoryScores: CategoryScore[];
  rawMetrics: MetricValue[];
  hasData: boolean;
  hasDigest: boolean;
  aiOverallScore?: number;
  aiCategoryScores?: Record<string, number>;
  aiCategoryAssessments?: Record<string, string>;
  digestMeta?: {
    measurement_date?: string;
    processing_status?: string;
    categories_processed?: string[];
    overall_score?: number;
    overall_weighted_score?: number;
    account_name?: string;
    organization_name?: string;
  };
  city?: string;
  country?: string;
  region?: string;
  latitude?: number;
  longitude?: number;
  primary_sponsors?: string | null;
  primary_sponsors_id?: string | null;
  secondary_sponsors?: string | null;
  secondary_sponsors_id?: string | null;
}

export interface MetricsConfiguration {
  includedCategories: string[];
  categoryWeights: Record<string, number>;
  metricWeights: Record<string, Record<string, number>>;
  includedMetrics: Record<string, string[]>;
  categoryOrder?: string[];
}

export interface HawkeyeMetricsAccountsInput {
  selectedAccounts: AccountInfo[];
  allWatchlistAccounts: AccountInfo[];
  watchlistId: string | null;
  selectedMeasurementDate: string | null;
  latestMeasurementDate: string | null;
  digestFromDate?: string | null;
  digestToDate?: string | null;
}

export interface AccountInfo {
  id: string;
  name: string;
  city?: string;
  country?: string;
  region?: string;
  latitude?: number;
  longitude?: number;
  primary_sponsors?: string | null;
  primary_sponsors_id?: string | null;
  secondary_sponsors?: string | null;
  secondary_sponsors_id?: string | null;
}

const DEFAULT_CATEGORY_WEIGHTS: Record<string, number> = {
  health: 20,
  experience: 20,
  engagement: 20,
  financial: 15,
  adoption: 15,
  support: 10,
};

const DEFAULT_METRIC_WEIGHTS: Record<string, Record<string, number>> = {
  health: {
    health_score: 30,
    risk_score: 25,
    strategic_value: 20,
    platform_dependency: 15,
    relationship_health: 10,
  },
  experience: { nps: 60, csat: 40 },
  engagement: {
    customer_responsiveness: 40,
    active_users: 20,
    feature_usage: 20,
    repeat_users: 20,
  },
  financial: {
    arr: 20,
    clv: 15,
    gross_retention: 15,
    logo_retention: 15,
    mrr: 10,
    net_retention: 10,
    churn_rate: 10,
    clv_cac_ratio: 5,
  },
  adoption: {
    license_utilization: 50,
    license_activity: 30,
    onboarding_status: 20,
  },
  support: {
    escalations: 25,
    escalation_trend: 25,
    open_tickets: 20,
    support_volume: 15,
    ticket_resolution_time: 15,
  },
};

const DEFAULT_INCLUDED_METRICS: Record<string, string[]> = {
  health: [
    "health_score",
    "risk_score",
    "strategic_value",
    "platform_dependency",
    "relationship_health",
  ],
  experience: ["nps", "csat"],
  engagement: [
    "customer_responsiveness",
    "active_users",
    "feature_usage",
    "repeat_users",
  ],
  financial: [
    "arr",
    "clv",
    "gross_retention",
    "logo_retention",
    "mrr",
    "net_retention",
    "churn_rate",
    "clv_cac_ratio",
  ],
  adoption: ["license_utilization", "license_activity", "onboarding_status"],
  support: [
    "escalations",
    "escalation_trend",
    "open_tickets",
    "support_volume",
    "ticket_resolution_time",
  ],
};

const userPreferences = null as {
  excluded_categories?: string[];
  excluded_metrics?: string[];
  category_order?: string[];
} | null;

export function getDefaultMetricsConfiguration(): MetricsConfiguration {
  return {
    includedCategories: Object.keys(DEFAULT_CATEGORY_WEIGHTS),
    categoryWeights: { ...DEFAULT_CATEGORY_WEIGHTS },
    metricWeights: JSON.parse(JSON.stringify(DEFAULT_METRIC_WEIGHTS)),
    includedMetrics: JSON.parse(JSON.stringify(DEFAULT_INCLUDED_METRICS)),
    categoryOrder: Object.keys(DEFAULT_CATEGORY_WEIGHTS),
  };
}

export type HawkeyeDigestState = {
  digestAccountIds: Set<string>;
  digestAiScores: Map<string, Record<string, number>>;
  digestAiAssessments: Map<string, Record<string, string>>;
  digestMetricsByAccount: Map<string, MetricValue[]>;
  digestMetaByAccount: Map<
    string,
    {
      measurement_date?: string;
      processing_status?: string;
      categories_processed?: string[];
      overall_score?: number;
      overall_weighted_score?: number;
      account_name?: string;
      organization_name?: string;
    }
  >;
};

export function emptyDigestState(): HawkeyeDigestState {
  return {
    digestAccountIds: new Set(),
    digestAiScores: new Map(),
    digestAiAssessments: new Map(),
    digestMetricsByAccount: new Map(),
    digestMetaByAccount: new Map(),
  };
}

export function resolveDigestDateRange(
  accountsInput: HawkeyeMetricsAccountsInput | undefined,
  measurementDate: string | null | undefined
): { digestFromDate: string | null; digestToDate: string | null } {
  const explicitFrom = (accountsInput?.digestFromDate ?? "").toString().trim();
  const explicitTo = (accountsInput?.digestToDate ?? "").toString().trim();
  if (explicitFrom) {
    return {
      digestFromDate: explicitFrom,
      digestToDate: explicitTo || explicitFrom,
    };
  }
  const single = (
    measurementDate ??
    accountsInput?.selectedMeasurementDate ??
    accountsInput?.latestMeasurementDate ??
    ""
  )
    .toString()
    .trim();
  const digestFromDate = single || null;
  return { digestFromDate, digestToDate: digestFromDate };
}

export function normalizeAccounts(
  accountsInput: HawkeyeMetricsAccountsInput | undefined
): { rowAccounts: AccountInfo[]; fetchAccounts: AccountInfo[] } {
  const accounts: AccountInfo[] = (accountsInput?.selectedAccounts ?? []).map(
    a => ({
      id: a.id,
      name: a.name,
      city: a.city,
      country: a.country,
      region: a.region,
      latitude: a.latitude,
      longitude: a.longitude,
      primary_sponsors: a.primary_sponsors,
      primary_sponsors_id: a.primary_sponsors_id,
      secondary_sponsors: a.secondary_sponsors,
      secondary_sponsors_id: a.secondary_sponsors_id,
    })
  );
  const allWatchlistAccounts: AccountInfo[] = (
    accountsInput?.allWatchlistAccounts ?? []
  ).map(a => ({
    id: a.id,
    name: a.name,
    city: a.city,
    country: a.country,
    region: a.region,
    latitude: a.latitude,
    longitude: a.longitude,
    primary_sponsors: a.primary_sponsors,
    primary_sponsors_id: a.primary_sponsors_id,
    secondary_sponsors: a.secondary_sponsors,
    secondary_sponsors_id: a.secondary_sponsors_id,
  }));
  const fetchAccounts =
    allWatchlistAccounts.length > 0 ? allWatchlistAccounts : accounts;
  return { rowAccounts: accounts, fetchAccounts };
}

const parseDigestNumeric = (value: unknown): number | undefined => {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string") {
    const parsed = parseFloat(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return undefined;
};

function extractDigestRows(response: any): {
  account_id: string;
  account_name?: string;
  organization_name?: string;
  consolidated_analysis: unknown;
  overall_score?: number;
  overall_weighted_score?: number;
  measurement_date?: string;
  categories_processed?: string[];
  processing_status?: string;
}[] {
  const content = response?.content;
  if (Array.isArray(content?.rows)) {
    return content.rows
      .filter((row: any) => row?.account_id && row?.consolidated_analysis)
      .map((row: any) => ({
        account_id: row.account_id,
        account_name: row.account_name,
        organization_name: row.organization_name,
        consolidated_analysis: row.consolidated_analysis,
        overall_score: parseDigestNumeric(row.overall_score),
        overall_weighted_score: parseDigestNumeric(row.overall_weighted_score),
        measurement_date:
          typeof row.measurement_date === "string"
            ? row.measurement_date
            : undefined,
        categories_processed: Array.isArray(row.categories_processed)
          ? row.categories_processed
          : undefined,
        processing_status:
          typeof row.processing_status === "string"
            ? row.processing_status
            : undefined,
      }));
  }

  const c = content ?? response?.data ?? response;
  if (Array.isArray(c)) {
    return c
      .filter((row: any) => row?.account_id && row?.consolidated_analysis)
      .map((row: any) => ({
        account_id: row.account_id,
        account_name: row.account_name,
        organization_name: row.organization_name,
        consolidated_analysis: row.consolidated_analysis,
        overall_score: parseDigestNumeric(row.overall_score),
        overall_weighted_score: parseDigestNumeric(row.overall_weighted_score),
        measurement_date:
          typeof row.measurement_date === "string"
            ? row.measurement_date
            : undefined,
        categories_processed: Array.isArray(row.categories_processed)
          ? row.categories_processed
          : undefined,
        processing_status:
          typeof row.processing_status === "string"
            ? row.processing_status
            : undefined,
      }));
  }
  return [];
}

function parseDigestResults(
  digestResults: {
    account_id: string;
    account_name?: string;
    organization_name?: string;
    consolidated_analysis: unknown;
    overall_score?: number;
    overall_weighted_score?: number;
    measurement_date?: string;
    categories_processed?: string[];
    processing_status?: string;
  }[]
): HawkeyeDigestState {
  const digestAccountIds = new Set<string>();
  const digestAiScores = new Map<string, Record<string, number>>();
  const digestAiAssessments = new Map<string, Record<string, string>>();
  const digestMetricsByAccount = new Map<string, MetricValue[]>();
  const digestMetaByAccount = new Map<
    string,
    {
      measurement_date?: string;
      processing_status?: string;
      categories_processed?: string[];
      overall_score?: number;
      overall_weighted_score?: number;
      account_name?: string;
      organization_name?: string;
    }
  >();

  const CATEGORY_ALIAS: Record<string, string> = {
    support_risk: "support",
    relationship: "relationships",
  };

  const toNumber = (v: unknown): number | undefined => {
    if (typeof v === "number" && Number.isFinite(v)) return v;
    if (typeof v === "string") {
      const n = parseFloat(v);
      if (Number.isFinite(n)) return n;
    }
    return undefined;
  };

  (digestResults || []).forEach(d => {
    const analysis = d.consolidated_analysis;
    if (!analysis || typeof analysis !== "object") return;
    const categoryScores: Record<string, number> = {};
    const categoryAssessments: Record<string, string> = {};
    const digestMetrics: MetricValue[] = [];

    for (const [rawKey, value] of Object.entries(
      analysis as Record<string, unknown>
    )) {
      if (rawKey === "summary" || !value || typeof value !== "object") continue;
      const canonicalKey = CATEGORY_ALIAS[rawKey] || rawKey;
      const v = value as Record<string, unknown>;
      const inputMetrics = Array.isArray(v._input_metrics)
        ? (v._input_metrics as Array<Record<string, unknown>>)
        : [];
      inputMetrics.forEach(m => {
        const metricId = String(m.metric_id || "").trim();
        if (!metricId) return;
        digestMetrics.push({
          metric_id: metricId,
          metric_name: String(m.metric_name || metricId),
          category: canonicalKey,
          category_id: canonicalKey,
          subcategory: "",
          value:
            m.current_value != null && m.current_value !== ""
              ? String(m.current_value)
              : "",
          normalized_value:
            toNumber(m.normalized_score) ??
            toNumber(m.benchmark_percentile) ??
            0,
          measurement_date: d.measurement_date ?? "",
          unit_of_measure:
            typeof m.unit_of_measure === "string" ? m.unit_of_measure : "",
          format: "",
          scale: "",
          benchmark_percentile: toNumber(m.benchmark_percentile),
          previous_value:
            m.previous_value != null && m.previous_value !== ""
              ? String(m.previous_value)
              : undefined,
          trend_direction:
            typeof m.trend_direction === "string"
              ? m.trend_direction
              : undefined,
          data_quality:
            typeof m.data_quality === "string" ? m.data_quality : undefined,
          change_percentage: toNumber(m.change_percentage),
        });
      });

      const score =
        toNumber(v.weighted_category_score) ??
        toNumber(v.category_score) ??
        toNumber(v.score);

      if (score !== undefined) {
        categoryScores[canonicalKey] = score;
      }

      const assessmentText = v.overall_assessment ?? v.assessment;
      if (typeof assessmentText === "string" && assessmentText.trim()) {
        categoryAssessments[canonicalKey] = assessmentText.trim();
      }
    }

    const summaryObj =
      (analysis as Record<string, unknown>).summary ??
      (analysis as Record<string, unknown>).overall;
    if (summaryObj && typeof summaryObj === "object") {
      const so = summaryObj as Record<string, unknown>;
      const execSummary =
        so.executive_summary ?? so.account_synopsis ?? so.synopsis;
      if (typeof execSummary === "string" && execSummary.trim()) {
        categoryAssessments.overall = execSummary.trim();
      }
    }

    // Native KPI Pass-2 shape — overall_synopsis.synopsis
    if (!categoryAssessments.overall) {
      const overallSynopsis = (analysis as Record<string, unknown>)
        .overall_synopsis;
      if (overallSynopsis && typeof overallSynopsis === "object") {
        const syn = (overallSynopsis as Record<string, unknown>).synopsis;
        if (typeof syn === "string" && syn.trim()) {
          categoryAssessments.overall = syn.trim();
        }
      }
    }

    if (
      !categoryAssessments.overall &&
      Object.keys(categoryAssessments).length > 0
    ) {
      categoryAssessments.overall =
        Object.values(categoryAssessments).join(" | ");
    }

    const topLevelOverallScore = toNumber(d.overall_weighted_score);
    if (topLevelOverallScore !== undefined) {
      categoryScores.overall = topLevelOverallScore;
    }

    if (Object.keys(categoryScores).length > 0) {
      digestAiScores.set(d.account_id, categoryScores);
    }
    if (Object.keys(categoryAssessments).length > 0) {
      digestAiAssessments.set(d.account_id, categoryAssessments);
    }
    if (digestMetrics.length > 0) {
      digestMetricsByAccount.set(d.account_id, digestMetrics);
    }
    digestMetaByAccount.set(d.account_id, {
      measurement_date: d.measurement_date,
      processing_status: d.processing_status,
      categories_processed: d.categories_processed,
      overall_score: d.overall_score,
      overall_weighted_score: d.overall_weighted_score,
      account_name: d.account_name,
      organization_name: d.organization_name,
    });
    if (
      Object.keys(categoryScores).length > 0 ||
      Object.keys(categoryAssessments).length > 0 ||
      digestMetrics.length > 0
    ) {
      digestAccountIds.add(d.account_id);
    }
  });

  return {
    digestAccountIds,
    digestAiScores,
    digestAiAssessments,
    digestMetricsByAccount,
    digestMetaByAccount,
  };
}

export async function loadMetricsConfigurationFromApi(): Promise<MetricsConfiguration> {
  let loadedConfig = getDefaultMetricsConfiguration();
  try {
    const savedConfig = await Store.dispatch(
      fetchMetricsOrgConfiguration()
    ).unwrap();

    if (savedConfig) {
      const overrideCategories = Array.isArray(
        savedConfig?.config?.config_override?.categories
      )
        ? savedConfig.config.config_override.categories
        : Array.isArray(savedConfig?.config_override?.categories)
          ? savedConfig.config_override.categories
          : Array.isArray(savedConfig?.config?.categories)
            ? savedConfig.config.categories
            : Array.isArray(savedConfig?.effective_config?.categories)
              ? savedConfig.effective_config.categories
              : [];

      const nextIncludedCategories: string[] = [];
      const nextCategoryWeights: Record<string, number> = {};
      const nextMetricWeights: Record<string, Record<string, number>> = {};
      const nextIncludedMetrics: Record<string, string[]> = {};
      const nextCategoryOrder: string[] = [];

      overrideCategories.forEach((category: any) => {
        const categoryName = String(category?.name || "");
        if (!categoryName) return;
        nextCategoryOrder.push(categoryName);
        nextCategoryWeights[categoryName] = Number(category?.weight) || 0;
        if (category?.enabled !== false) {
          nextIncludedCategories.push(categoryName);
        }
        const metrics = Array.isArray(category?.metrics) ? category.metrics : [];
        nextMetricWeights[categoryName] = {};
        nextIncludedMetrics[categoryName] = [];
        metrics.forEach((metric: any) => {
          const metricName = String(metric?.name || "");
          if (!metricName) return;
          nextMetricWeights[categoryName][metricName] =
            Number(metric?.weight) || 0;
          if (metric?.enabled) {
            nextIncludedMetrics[categoryName].push(metricName);
          }
        });
      });

      const hasOverrideConfig = overrideCategories.length > 0;
      loadedConfig = {
        includedCategories: hasOverrideConfig
          ? nextIncludedCategories
          : savedConfig.included_categories ||
            Object.keys(DEFAULT_CATEGORY_WEIGHTS),
        categoryWeights: hasOverrideConfig
          ? nextCategoryWeights
          : savedConfig.category_weights || DEFAULT_CATEGORY_WEIGHTS,
        metricWeights: hasOverrideConfig
          ? nextMetricWeights
          : savedConfig.metric_weights || DEFAULT_METRIC_WEIGHTS,
        includedMetrics: hasOverrideConfig
          ? nextIncludedMetrics
          : savedConfig.included_metrics || DEFAULT_INCLUDED_METRICS,
        categoryOrder: hasOverrideConfig
          ? nextCategoryOrder
          : savedConfig.category_order ||
            Object.keys(DEFAULT_CATEGORY_WEIGHTS),
      };

      if (userPreferences) {
        if (userPreferences.excluded_categories?.length) {
          loadedConfig.includedCategories =
            loadedConfig.includedCategories.filter(
              cat => !userPreferences.excluded_categories!.includes(cat)
            );
        }
        if (userPreferences.excluded_metrics?.length) {
          const excludedSet = new Set(userPreferences.excluded_metrics);
          const newIncludedMetrics = { ...loadedConfig.includedMetrics };
          for (const categoryId of Object.keys(newIncludedMetrics)) {
            newIncludedMetrics[categoryId] = newIncludedMetrics[
              categoryId
            ].filter(metricId => !excludedSet.has(metricId));
          }
          loadedConfig.includedMetrics = newIncludedMetrics;
        }
        if (userPreferences.category_order?.length) {
          loadedConfig.categoryOrder = userPreferences.category_order;
        }
      }
    }
  } catch (e) {
    console.error("Error loading metrics configuration:", e);
  }
  return loadedConfig;
}

const hawkeyeDigestInflight = new Map<string, Promise<HawkeyeDigestState>>();

const getHawkeyeDigestRequestKey = (
  accountIds: string[],
  digestFromDate: string | null,
  digestToDate: string | null,
  requireDate: boolean,
  dataModeKey: string = "live"
): string => {
  const sortedIds = [...accountIds].map(String).sort().join(",");
  return `${sortedIds}|${digestFromDate ?? ""}|${digestToDate ?? ""}|${requireDate}|${dataModeKey}`;
};

export async function fetchHawkeyeDigestState(
  accountIds: string[],
  digestFromDate: string | null,
  digestToDate: string | null,
  requireDate: boolean,
  dataModeKey: string = "live"
): Promise<HawkeyeDigestState> {
  if (!accountIds.length) return emptyDigestState();
  if (requireDate && (!digestFromDate || !digestToDate)) {
    return emptyDigestState();
  }

  const requestKey = getHawkeyeDigestRequestKey(
    accountIds,
    digestFromDate,
    digestToDate,
    requireDate,
    dataModeKey
  );
  const inflight = hawkeyeDigestInflight.get(requestKey);
  if (inflight) return inflight;

  const promise = (async (): Promise<HawkeyeDigestState> => {
    try {
      const digestPayload =
        requireDate && digestFromDate && digestToDate
          ? {
              account_ids: accountIds,
              from_date: digestFromDate,
              to_date: digestToDate,
            }
          : {
              account_ids: accountIds,
            };
      const digestResponse = await Store.dispatch(
        fetchHawkeyeDigest(digestPayload)
      ).unwrap();
      const digestRows = extractDigestRows(digestResponse);
      return parseDigestResults(digestRows);
    } catch (e) {
      console.warn("Failed to load Hawkeye digests:", e);
      return emptyDigestState();
    }
  })();

  hawkeyeDigestInflight.set(requestKey, promise);
  try {
    return await promise;
  } finally {
    hawkeyeDigestInflight.delete(requestKey);
  }
}

export function buildAccountMatrixData(
  rowAccounts: AccountInfo[],
  configuration: MetricsConfiguration,
  digest: HawkeyeDigestState
): AccountMatrixData[] {
  const result: AccountMatrixData[] = [];
  const {
    digestAccountIds,
    digestAiScores,
    digestAiAssessments,
    digestMetricsByAccount,
    digestMetaByAccount,
  } = digest;

  rowAccounts.forEach(account => {
    const aiCategoryScoresForAccount = digestAiScores.get(account.id);
    const digestRawMetrics = digestMetricsByAccount.get(account.id) || [];
    const digestMeta = digestMetaByAccount.get(account.id);
    // Ignore catalog zero-fillers (missing metrics stamped as value=0 / poor).
    // Real Divum rows have data_quality from import (often "good"/100).
    const realMetrics = digestRawMetrics.filter(m => {
      const raw = (m.value ?? "").toString().trim();
      const norm = Number(m.normalized_value);
      if (raw !== "" && raw !== "0") return true;
      if (Number.isFinite(norm) && norm > 0) return true;
      return false;
    });
    const categoryScores: CategoryScore[] = configuration.includedCategories.map(
      category => {
        const catMetrics = realMetrics.filter(
          m => (m.category_id || m.category) === category
        );
        const includedMetricIds = configuration.includedMetrics[category] || [];
        const metricWeights = configuration.metricWeights[category] || {};

        let weightedSum = 0;
        let totalWeight = 0;
        for (const metricId of includedMetricIds) {
          const m = catMetrics.find(x => x.metric_id === metricId);
          if (!m) continue;
          const weight = Number(metricWeights[metricId] ?? 0);
          if (!Number.isFinite(weight) || weight <= 0) continue;
          const normalized = Number(m.normalized_value);
          if (!Number.isFinite(normalized)) continue;
          weightedSum += normalized * weight;
          totalWeight += weight;
        }

        const computedScore = totalWeight > 0 ? weightedSum / totalWeight : null;
        const aiScore = aiCategoryScoresForAccount?.[category];
        // Prefer digest category_score (same source digitalocean overlays) so
        // both toggles show identical dots for the same period.
        const score =
          typeof aiScore === "number" && Number.isFinite(aiScore)
            ? aiScore
            : computedScore !== null
              ? computedScore
              : 0;

        return {
          category,
          score,
          metrics: catMetrics,
          aiScore:
            typeof aiScore === "number" && Number.isFinite(aiScore)
              ? aiScore
              : undefined,
        };
      }
    );

    const aiOverallScore =
      aiCategoryScoresForAccount &&
      typeof aiCategoryScoresForAccount.overall === "number"
        ? aiCategoryScoresForAccount.overall
        : undefined;

    const digestOverall =
      typeof digestMeta?.overall_score === "number" &&
      Number.isFinite(digestMeta.overall_score)
        ? digestMeta.overall_score
        : typeof digestMeta?.overall_weighted_score === "number" &&
            Number.isFinite(digestMeta.overall_weighted_score)
          ? digestMeta.overall_weighted_score
          : undefined;

    const computedOverall = (() => {
      let weightedSum = 0;
      let totalWeight = 0;
      for (const cat of categoryScores) {
        const w = Number(configuration.categoryWeights?.[cat.category] ?? 0);
        if (!Number.isFinite(w) || w <= 0) continue;
        if (!Number.isFinite(cat.score) || cat.metrics.length === 0) continue;
        weightedSum += cat.score * w;
        totalWeight += w;
      }
      return totalWeight > 0 ? weightedSum / totalWeight : null;
    })();

    // Prefer digest overall (same Hawkeye rollup digitalocean overlays).
    const overallScore =
      aiOverallScore ?? digestOverall ?? computedOverall ?? 0;

    result.push({
      account_id: account.id,
      account_name: account.name,
      overallScore,
      categoryScores,
      rawMetrics: realMetrics,
      hasData:
        categoryScores.some(c => c.metrics.length > 0) ||
        computedOverall !== null ||
        aiOverallScore !== undefined ||
        digestOverall !== undefined ||
        realMetrics.length > 0,
      hasDigest: digestAccountIds.has(account.id),
      aiOverallScore,
      aiCategoryScores: aiCategoryScoresForAccount,
      aiCategoryAssessments: digestAiAssessments.get(account.id),
      digestMeta,
      city: account.city,
      country: account.country,
      region: account.region,
      latitude: account.latitude,
      longitude: account.longitude,
      primary_sponsors: account.primary_sponsors,
      primary_sponsors_id: account.primary_sponsors_id,
      secondary_sponsors: account.secondary_sponsors,
      secondary_sponsors_id: account.secondary_sponsors_id,
    });
  });

  return result;
}

export function getAvailableCategories(
  digestAiScores: Map<string, Record<string, number>>
): string[] {
  const categories = new Set<string>();
  digestAiScores.forEach(scores => {
    Object.keys(scores).forEach(category => {
      if (category !== "overall") {
        categories.add(category);
      }
    });
  });
  return Array.from(categories).sort();
}

/** Props assembled on HawkeyePage from direct API calls; charts consume this instead of calling the hook. */
export interface HawkeyeChartMetricsProps {
  matrixData: AccountMatrixData[];
  loading: boolean;
  error: string | null;
  configuration: MetricsConfiguration;
  updateConfiguration: (newConfig: Partial<MetricsConfiguration>) => void;
  availableCategories: string[];
  orderedCategories: string[];
  refetch: () => Promise<void>;
  reloadConfiguration: () => Promise<void>;
}

export function getOrderedCategories(
  configuration: MetricsConfiguration,
  availableCategories: string[]
): string[] {
  if (!configuration.categoryOrder?.length) {
    return availableCategories;
  }
  const orderedList = configuration.categoryOrder.filter(category =>
    configuration.includedCategories.includes(category)
  );
  const missingCategories = availableCategories.filter(
    category =>
      configuration.includedCategories.includes(category) &&
      !configuration.categoryOrder!.includes(category)
  );
  return [...orderedList, ...missingCategories];
}
